import { canOrg, canPlatform, type OrgAction, type OrgRole, type PlatformRole } from '@1qa/shared';

export type NavIcon =
  | 'dashboard'
  | 'projects'
  | 'reports'
  | 'members'
  | 'orgs'
  | 'users'
  | 'invitations'
  | 'plugins'
  | 'ai'
  | 'testdata'
  | 'audit'
  | 'guide'
  | 'helpers';

export interface NavItem {
  to: string;
  label: string;
  icon: NavIcon;
  section: 'main' | 'settings' | 'admin' | 'help';
}

/** The product guide: for everyone, Admins included. */
export const GUIDE_NAV: NavItem = { to: '/guide', label: 'How to use', icon: 'guide', section: 'help' };

/**
 * Builds the sidebar for the current user in the current organization. Everyone also gets "How to use".
 * Viewers (and users without an org role) only see Reports. Admins are not part of any organization
 * and only get the Administration section; AI models and plugins belong to Project Admins.
 */
export function buildNav(platformRole: PlatformRole, orgRole: OrgRole | null): NavItem[] {
  if (platformRole === 'ADMIN') return [...adminNav(platformRole), GUIDE_NAV];
  const org = (action: OrgAction) => canOrg(platformRole, orgRole, action);
  const items: NavItem[] = [];

  if (org('run.read')) items.push({ to: '/', label: 'Dashboard', icon: 'dashboard', section: 'main' });
  if (org('project.read')) items.push({ to: '/projects', label: 'Test projects', icon: 'projects', section: 'main' });
  if (org('test.read')) items.push({ to: '/test-data', label: 'Test data', icon: 'testdata', section: 'main' });
  // Browser recorder, Devices and later services: one page with a sub-page each (features/helpers/services.ts).
  if (org('test.write') || org('run.trigger')) items.push({ to: '/helpers', label: 'Helper services', icon: 'helpers', section: 'main' });
  items.push({ to: '/reports', label: 'Reports', icon: 'reports', section: 'main' });
  if (org('org.members.read')) items.push({ to: '/members', label: 'Members', icon: 'members', section: 'main' });
  if (canPlatform(platformRole, 'org.create') || orgRole === 'PROJECT_ADMIN') {
    items.push({ to: '/orgs', label: 'Organizations', icon: 'orgs', section: 'main' });
  }

  if (org('llm.manage')) items.push({ to: '/ai-models', label: 'AI models', icon: 'ai', section: 'settings' });
  if (org('plugins.manage')) items.push({ to: '/plugins', label: 'Plugins', icon: 'plugins', section: 'settings' });
  items.push(GUIDE_NAV);
  return items;
}

function adminNav(platformRole: PlatformRole): NavItem[] {
  const items: NavItem[] = [];
  if (canPlatform(platformRole, 'platform.audit.read')) {
    items.push({ to: '/admin/audit', label: 'Audit dashboard', icon: 'audit', section: 'admin' });
  }
  if (canPlatform(platformRole, 'platform.orgs.readAll')) {
    items.push({ to: '/admin/organizations', label: 'Organizations', icon: 'orgs', section: 'admin' });
  }
  if (canPlatform(platformRole, 'platform.users.manage')) {
    items.push({ to: '/admin/users', label: 'Users', icon: 'users', section: 'admin' });
  }
  if (canPlatform(platformRole, 'platform.invite.projectAdmin')) {
    items.push({ to: '/admin/invitations', label: 'Invitations', icon: 'invitations', section: 'admin' });
  }
  return items;
}

/** Where to land after login: Admins on the audit dashboard, viewers straight on their reports. */
export function homePath(platformRole: PlatformRole, orgRole: OrgRole | null): string {
  if (canPlatform(platformRole, 'platform.audit.read')) return '/admin/audit';
  return canOrg(platformRole, orgRole, 'run.read') ? '/' : '/reports';
}
